import { useCallback, useEffect, useRef } from 'react'

import { BUTTON_PRESS, ClipLoaderEvents, trackEvent } from '@/utils/analytics'

import { MixpanelDataIProps } from '../types/types'

interface UseClipLoaderTrackingIProps {
  incidentId: string | undefined
  mixpanelData: MixpanelDataIProps
  hasTimedOut: boolean
  hasVideoClip: boolean
  hasClickedLive: boolean
}

/**
 * Manages all Mixpanel tracking for the ClipLoader feature.
 *
 * Tracks:
 * - TimedOut: when timeout occurs after ClipLoader was shown
 * - ClipAvailable (Live/Collapsed): when clip arrives after ClipLoader was shown
 * - ViewedLive: when user clicks Live button
 * - ClipClicked: when user clicks Clip button
 *
 * Note: "Shown" event is tracked by ClipLoader component itself on mount.
 */
export const useClipLoaderTracking = ({
  incidentId,
  mixpanelData,
  hasTimedOut,
  hasVideoClip,
  hasClickedLive,
}: UseClipLoaderTrackingIProps) => {
  // Tracking refs
  const prevIncidentIdRef = useRef(incidentId)
  const hasShownClipLoader = useRef(false)
  const hasTrackedTimeout = useRef(false)
  const hasTrackedClipAvailable = useRef(false)
  const hasTrackedViewedLive = useRef(false)
  const hasTrackedClipClicked = useRef(false)

  // Reset refs synchronously when incident changes
  if (prevIncidentIdRef.current !== incidentId) {
    prevIncidentIdRef.current = incidentId
    hasShownClipLoader.current = false
    hasTrackedTimeout.current = false
    hasTrackedClipAvailable.current = false
    hasTrackedViewedLive.current = false
    hasTrackedClipClicked.current = false
  }

  // Track TimedOut when timeout occurs after ClipLoader was shown (Not when live steam is shown)
  useEffect(() => {
    if (
      hasShownClipLoader.current &&
      hasTimedOut &&
      !hasVideoClip &&
      !hasTrackedTimeout.current
    ) {
      hasTrackedTimeout.current = true
      trackEvent(ClipLoaderEvents.timedOut, mixpanelData)
    }
  }, [hasTimedOut, hasVideoClip, mixpanelData])

  // Track ClipAvailable when clip arrives after ClipLoader was shown
  useEffect(() => {
    if (
      hasShownClipLoader.current &&
      hasVideoClip &&
      !hasTrackedClipAvailable.current
    ) {
      hasTrackedClipAvailable.current = true
      const event = hasClickedLive
        ? ClipLoaderEvents.clipAvailableLive
        : ClipLoaderEvents.clipAvailableCollapsed
      trackEvent(event, mixpanelData)
    }
  }, [hasVideoClip, hasClickedLive, mixpanelData])

  // Callback for ClipLoader to notify when shown
  const onClipLoaderShown = useCallback(() => {
    hasShownClipLoader.current = true
  }, [])

  // Button tracking callbacks
  const trackViewedLive = useCallback(() => {
    if (!hasTrackedViewedLive.current) {
      hasTrackedViewedLive.current = true
      trackEvent(`${BUTTON_PRESS} ${ClipLoaderEvents.viewedLive}`, mixpanelData)
    }
  }, [mixpanelData])

  const trackClipClicked = useCallback(() => {
    if (!hasTrackedClipClicked.current) {
      hasTrackedClipClicked.current = true
      trackEvent(
        `${BUTTON_PRESS} ${ClipLoaderEvents.clipClicked}`,
        mixpanelData
      )
    }
  }, [mixpanelData])

  return {
    onClipLoaderShown,
    trackViewedLive,
    trackClipClicked,
  }
}
